'use client';

import { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Button, Chip, TextField, cn } from '@crabs-ai/ds-react';
import {
  MusicNote,
  Photo,
  PhotoFill,
  Search,
  Videocam,
  VideocamFill,
  Close,
  BookmarkFill,
  Bookmark,
} from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { GetFolderItemsContentResponseType } from '@/shared/apis/folder/get-folder-items';
import { useGetFolderHierarchyWithSharedApi } from '@/shared/apis/folder/get-folder-hierarchy-with-shared';
import { DtoContentType, DtoFolderType } from '@/shared/constants';
import { useAssetPicker, FolderType } from './useAssetPicker';
import { AssetPickerBreadcrumb } from './AssetPickerBreadcrumb';
import { AssetPickerGrid } from './AssetPickerGrid';

export interface AssetPickerModalProps {
  isOpen: boolean;
  spaceId: number;
  rootFolderId: number;
  onClose: () => void;
  onSelect: (items: GetFolderItemsContentResponseType[]) => void;
  /**
   * 허용할 콘텐츠 타입 목록
   * - Editor: [video, image, audio] (모두)
   * - MTS: [video, image]
   * - LTS: [video]
   * 지정하지 않으면 모든 타입 허용
   */
  contentTypes?: DtoContentType[];
  /** 모달 타이틀 (기본값: 'Editor.asset-picker.title') */
  title?: string;
  /** 최대 선택 가능 개수 (지정하지 않으면 무제한, 1이면 단일 선택) */
  maxSelections?: number;
}

/** 콘텐츠 타입별 필터 옵션 설정 */
const CONTENT_TYPE_FILTER_CONFIG = {
  [DtoContentType.video]: {
    label: 'Editor.asset-panel.video',
    idleIcon: <Videocam className="size-md" />,
    activeIcon: <VideocamFill className="size-md" />,
  },
  [DtoContentType.image]: {
    label: 'Editor.asset-panel.image',
    idleIcon: <Photo className="size-md" />,
    activeIcon: <PhotoFill className="size-md" />,
  },
  [DtoContentType.audio]: {
    label: 'Editor.asset-panel.audio',
    idleIcon: <MusicNote className="size-md" />,
    activeIcon: <MusicNote className="size-md" />,
  },
};

/**
 * 에셋 선택 모달 컴포넌트 (공통)
 * - 폴더 탐색
 * - 다중 선택
 * - 필터/검색
 * - contentTypes prop으로 허용할 콘텐츠 타입 지정 가능
 */
export const AssetPickerModal = memo(function AssetPickerModal({
  isOpen,
  spaceId,
  rootFolderId,
  onClose,
  onSelect,
  contentTypes,
  title,
  maxSelections,
}: AssetPickerModalProps) {
  const t = useTranslations();

  const { data: hierarchyWithSharedData } = useGetFolderHierarchyWithSharedApi(
    { spaceId },
    { enabled: isOpen && spaceId > 0 },
  );

  const userAssetFolder = useMemo(() => {
    if (!hierarchyWithSharedData?.data?.hierarchy?.childList) return null;
    return hierarchyWithSharedData.data.hierarchy.childList.find(
      (folder) => folder.type === DtoFolderType.USER_ASSET,
    );
  }, [hierarchyWithSharedData]);

  // 시작 폴더 ID (USER_ASSET 있으면 그것, 없으면 ROOT)
  const startFolderId = userAssetFolder?.id ?? rootFolderId;
  const startFolderType = userAssetFolder
    ? FolderType.USER_ASSET
    : FolderType.ROOT;
  const startFolderName = userAssetFolder
    ? 'folder.type.user_asset'
    : 'folder.type.root';

  const picker = useAssetPicker(
    startFolderId,
    startFolderType,
    startFolderName,
    contentTypes, // 허용할 콘텐츠 타입 전달
    maxSelections, // 최대 선택 개수 전달
  );

  // 검색 입력 상태 (디바운스용)
  const [searchInput, setSearchInput] = useState('');

  // 드래그 관련 상태
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement>(null);

  // 모달 열릴 때 위치 초기화
  useEffect(() => {
    if (isOpen) {
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // 드래그 시작
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      // 버튼이나 입력 요소에서 드래그 시작하지 않도록
      if ((e.target as HTMLElement).closest('button')) return;

      setIsDragging(true);
      dragStartPos.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [position],
  );

  // 드래그 중
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX - dragStartPos.current.x,
        y: e.clientY - dragStartPos.current.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // 검색 핸들러
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);
      picker.setSearchKeyword(value);
    },
    [picker],
  );

  // 확인 버튼 핸들러
  const handleConfirm = useCallback(() => {
    const selectedArray = Array.from(picker.selectedItems.values());
    onSelect(selectedArray);
    onClose();
  }, [picker.selectedItems, onSelect, onClose]);

  // 모달 닫기 핸들러 (선택 초기화)
  const handleClose = useCallback(() => {
    picker.clearSelection();
    setSearchInput('');
    picker.setSearchKeyword('');
    picker.setFilter(null);
    onClose();
  }, [picker, onClose]);

  // 배경 클릭 시 닫기
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  // 필터 옵션 동적 생성 (contentTypes에 따라)
  const filterOptions = useMemo(() => {
    const options: Array<{
      type: DtoContentType | null;
      label: string;
      idleIcon?: React.ReactNode;
      activeIcon?: React.ReactNode;
    }> = [{ type: null, label: t('Editor.asset-panel.all') }];

    // contentTypes가 지정되어 있으면 해당 타입만 추가
    const typesToShow = contentTypes ?? [
      DtoContentType.video,
      DtoContentType.image,
      DtoContentType.audio,
    ];

    typesToShow.forEach((type) => {
      const config =
        CONTENT_TYPE_FILTER_CONFIG[
          type as keyof typeof CONTENT_TYPE_FILTER_CONFIG
        ];
      if (config) {
        options.push({
          type,
          label: t(config.label),
          idleIcon: config.idleIcon,
          activeIcon: config.activeIcon,
        });
      }
    });

    return options;
  }, [contentTypes, t]);

  // 필터 옵션이 1개(전체)만 있으면 필터 숨김 (contentTypes가 1개인 경우)
  const showFilterChips = filterOptions.length > 2;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={cn(
          'flex h-[80vh] w-[90vw] max-w-[1200px] flex-col overflow-hidden',
          'rounded-[16px] border border-sys-style-neutral-light',
          'bg-sys-bg-surface shadow-2xl',
        )}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
      >
        {/* 헤더 (드래그 핸들) */}
        <div
          className={cn(
            'flex items-center justify-between',
            'border-b border-sys-style-neutral-light',
            'px-lg py-md',
            'cursor-move select-none',
          )}
          onMouseDown={handleDragStart}
        >
          <h2 className="text-title-large text-sys-cont-neutral-default">
            {title ?? t('Editor.asset-picker.title')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className={cn(
              'flex size-xl items-center justify-center rounded-full',
              'hover:bg-sys-bg-surface-hover',
              'active:bg-sys-bg-surface-press',
              'transition-colors',
            )}
          >
            <Close className="size-md [&_path]:fill-sys-cont-neutral-default" />
          </button>
        </div>

        {/* 브레드크럼 */}
        <div className="px-lg pt-md">
          <AssetPickerBreadcrumb
            path={picker.folderPath}
            onNavigate={picker.navigateToPathIndex}
          />
        </div>

        {/* 필터 + 검색 */}
        <div className="flex items-center justify-between gap-sm px-lg py-md">
          {/* 필터 */}
          <div className="flex items-center gap-sm">
            {/* 콘텐츠 타입 필터 (2개 이상일 때만 표시) */}
            {showFilterChips && (
              <div className="flex items-center gap-2xs">
                {filterOptions.map((option) => {
                  const isActive = option.type === picker.filter;
                  return (
                    <Chip
                      key={option.type ?? 'all'}
                      size="lg"
                      style="filled"
                      isShowLeadingIcon={!!option.idleIcon}
                      leadingIconNode={
                        isActive ? option.activeIcon : option.idleIcon
                      }
                      labelNode={option.label}
                      selected={isActive}
                      onClick={() => picker.setFilter(option.type)}
                    />
                  );
                })}
              </div>
            )}

            {/* 북마크 필터 */}
            {showFilterChips && (
              <div className="h-[24px] w-[1px] bg-sys-style-neutral-light" />
            )}
            <Chip
              size="lg"
              style="filled"
              isShowLeadingIcon
              leadingIconNode={
                picker.bookmarkOnly ? (
                  <BookmarkFill className="size-md" />
                ) : (
                  <Bookmark className="size-md" />
                )
              }
              labelNode={t('Common.bookmark')}
              selected={picker.bookmarkOnly}
              onClick={() => picker.setBookmarkOnly(!picker.bookmarkOnly)}
            />
          </div>

          {/* 검색 */}
          <div className="w-[240px]">
            <TextField
              isShowLeadingIcon
              leadingIconNode={
                <Search className="[&_path]:fill-sys-cont-neutral-light" />
              }
              placeholder={t('Common.search-placeholder')}
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        {/* 그리드 */}
        <div className="min-h-0 flex-1 overflow-hidden px-lg pb-md">
          <AssetPickerGrid
            items={picker.items}
            selectedItems={picker.selectedItems}
            isLoading={picker.isLoading}
            showTypeLabel={showFilterChips && picker.filter === null}
            isBookmarked={picker.isBookmarked}
            onSelect={picker.toggleSelect}
            onFolderClick={picker.navigateToFolder}
          />
        </div>

        {/* 푸터 */}
        <div
          className={cn(
            'flex items-center justify-between',
            'border-t border-sys-style-neutral-light',
            'bg-sys-bg-surface-1 px-lg py-md',
          )}
        >
          <span className="text-body-medium text-sys-cont-neutral-light">
            {t('Editor.asset-picker.selected-count', {
              count: picker.selectedItems.size,
            })}
          </span>
          <div className="flex items-center gap-sm">
            <Button
              hierarchy="tertiary"
              size="medium"
              onClick={handleClose}
              className="w-fit"
            >
              {t('Common.cancel')}
            </Button>
            <Button
              hierarchy="primary"
              size="medium"
              onClick={handleConfirm}
              disabled={picker.selectedItems.size === 0}
              className="w-fit"
            >
              {t('Common.select')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AssetPickerModal;
